import Alert from '@mui/material/Alert';
import React, { useState, useEffect, useContext } from 'react';
import { observer } from 'mobx-react';
import { ModalContext } from '../../../context-providers/ModalProvider';
import { ServicesContext } from '../../../context-providers/ServicesProvider';
import { tokenDecimals } from '../../../helpers/config/contracts.config';
import { getErrorMessage } from '../../../helpers/error-message.helper';
import { formatCurrencyAmount, getPositionNameWithColor } from '../../../helpers/formatters';
import { getNetPnlWithPosition } from '../../../helpers/math/position-math.helpers';
import { IPositionResponse } from '../../../services/api/api.interfaces';

import LabelValue from '../../LabelValue/LabelValue';
import NereusButton from '../../NereusButton/NereusButton';
import StatusIcon from '../../StatusIcon/StatusIcon';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import {useSwitchChainToBase} from "../../../hooks/useSwitchChainToBase";

interface ClosePositionProps {
    position: IPositionResponse;
}

const ctaDefaultText = 'Close Position';

const ClosePosition: React.FC<ClosePositionProps> = observer(({ position }) => {
    const { closeModal } = useContext(ModalContext);
    const { positionsManager } = useContext(ServicesContext);
    const switchChainToBase = useSwitchChainToBase();

    const [amountNode, setAmountNode] = useState<string | null>(null);
    const [collateralNode, setCollateralNode] = useState<string | null>(null);
    const [buttonText, setButtonText] = useState<string>(ctaDefaultText);
    const [pnl, setPnl] = useState<number | null>(null);
    const [isPending, setIsPending] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const pnl = Number(getNetPnlWithPosition(position));
        setPnl(pnl);

    }, [position.realizedPnL, position.netPnlAmount]);


    useEffect(() => {
        const collateral = Number(position.amount) / 10 ** tokenDecimals.usdc;
        const collateralWithProfit = Number(collateral) + Number(pnl);
        if (collateralWithProfit) {
            setButtonText(`Close and get ≈ ${formatCurrencyAmount(collateralWithProfit, position.quoteAsset?.toUpperCase())}`);
        } else {
            setButtonText(ctaDefaultText);
        }

    }, [pnl, position.amount]);

    const handleClosePosition = async () => {
        setIsPending(true);
        if (position.id) {
            try {
                await switchChainToBase()
                await positionsManager.closePosition(position.id);
                closeModal();
            } catch (err) {
                setError(getErrorMessage(err));
            }
        }

        setIsPending(false);
    }

    useEffect(() => {
        const collateral = Number(position.amount) / 10 ** tokenDecimals.usdc;
        const amount = collateral * Number(position.leverage);
        const price = Number(position.lastPrice) || Number(position.initialPrice);
        const amountInBaseAsset = amount / price;

        setCollateralNode(formatCurrencyAmount(collateral, position.quoteAsset?.toUpperCase()));
        setAmountNode(`${formatCurrencyAmount(amountInBaseAsset, position.baseAsset)}`);
    }, [position]);

    return (
        <ModalWrapper className="ClosePosition"
                      headerChildren={<h2 className="nereus-body-m">Close Position - {getPositionNameWithColor(position)}</h2>}
                      bodyChildren={<div className="ClosePosition__body">
                          <div>
                              <LabelValue label="Amount" value={amountNode} />
                              <LabelValue label="Collateral" value={collateralNode} />
                              <LabelValue label="Open Price" value={formatCurrencyAmount(position.initialPrice, '$')} />
                              <hr className="nereus-bg-neutral-dark-blue mt-4 mb-4"/>
                              {Number(position.lastPrice) ? <LabelValue label="Close Price" value={formatCurrencyAmount(position.lastPrice, '$')}/> : null}
                              <LabelValue label="Total Rollover Fee" value={<span className="nereus-color-status-down">{formatCurrencyAmount(-Number(position.feeAmount || 0), 'USDC')}</span>} />
                              {pnl ? <LabelValue label="Net Pnl" value={<span className={pnl >= 0 ? 'nereus-color-status-up' : 'nereus-color-status-down'}>{formatCurrencyAmount(pnl, 'USDC', 2)}</span>} />: null}

                          </div>

                          {error &&
                              <Alert className="WithdrawDialog__error mt-4" variant="standard" severity="error">
                                  <span className="nereus-caption">{error}</span>
                              </Alert>}

                          <div className="flex mt-4">
                              <NereusButton  variant="contained"
                                            onClick={handleClosePosition}
                                             disabled={isPending}
                                            className="bg-light-green text-dark-blue w-full">
                                  {buttonText}{isPending && <StatusIcon className="ml-1 inline" status="disabled-pending" />}
                              </NereusButton>
                          </div>
                      </div>}
        />);

});

export default ClosePosition;
